import { DatePickerField, Form, Modal } from '@/components';
import {
  useContinueProject,
  useProject,
} from '@/features/project/_hooks/project';
import { ContinueProjectSchema } from '@/features/project/projects/_models/project';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';

type FormValues = z.infer<typeof ContinueProjectSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const ContinueProjectModal = (props: Props) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const navigate = useNavigate();
  const { id } = useId();
  const { open, onClose } = props;
  const project = useProject(id);
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear: project.data ? dayjs().year(project.data.fiscalYear + 1) : '',
    },
    resolver: zodResolver(ContinueProjectSchema),
  });
  const { mutateAsync } = useContinueProject();

  const submitHandler = async (values: FormValues) => {
    const { continuedProjectId } = await mutateAsync({ ...values, id });
    await Promise.all([
      openMessage('success', t('message-completed')),
      (async () => {
        onClose();
        navigate(`/rs/project/${continuedProjectId}`);
      })(),
    ]);
  };

  return (
    <Modal
      title={t('confirm-continue-fiscal-year')}
      open={open}
      centered
      onOk={handleSubmit(submitHandler)}
      okText={t('continue-project')}
      onCancel={onClose}
      cancelText={t('cancel')}
      confirmLoading={isSubmitting}
    >
      <Form
        leavePageAlertCondition={null}
        layout="vertical"
        style={{ maxWidth: 480 }}
      >
        <DatePickerField
          name="fiscalYear"
          label={t('created-fiscal-year')}
          control={control}
          errors={errors}
          picker="year"
          disabled
        />
      </Form>
    </Modal>
  );
};
